/**
 * /transport/orders/:order_id — buyer-facing order detail
 * --------------------------------------------------------
 * Public access via UUID (the order id is the access token — same
 * pattern as Stripe Checkout success URLs). Buyer lands here:
 *   - After submitting a request (`?status=submitted`) → confirmation
 *   - After admin assigns a transporter → "Pay now" button appears
 *   - After paying via Stripe (`?status=paid`) → "Trip booked" state
 *
 * Polls every 8 seconds while in `awaiting_payment` and `booked` so
 * status transitions (admin assignment, payment confirmation via
 * webhook) reflect without a manual refresh.
 */
import React, { useState, useEffect, useCallback } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, useSearchParams, Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import ConsentResolutionPanel from "@/components/ConsentResolutionPanel";
import TripUpdatesPanel from "@/components/TripUpdatesPanel";
import { formatApiError } from "@/lib/utils-app";
import { TruckIcon as Truck, ShieldCheckIcon as ShieldCheck, ClockIcon as Clock, CheckCircleIcon as CheckCircle, MapPinIcon as MapPin, CurrencyDollarIcon as CurrencyDollar } from "@phosphor-icons/react";

const STATUS_PILL = {
  open_pending_assign: {
    label: "Awaiting match",
    tone: "bg-amber-50 border-amber-200 text-amber-800",
    blurb: "We're reviewing your request and matching you with a verified transporter.",
  },
  open_for_bids: {
    label: "Quotes coming in",
    tone: "bg-blue-50 border-blue-200 text-blue-800",
    blurb: "Verified transporters are reviewing your route. Most quotes arrive within 24 hours — pick the one you like best.",
  },
  awaiting_payment: {
    label: "Quote ready",
    tone: "bg-blue-50 border-blue-200 text-blue-800",
    blurb: "Your transporter is confirmed. Pay below to lock in the trip — funds are held in escrow until delivery.",
  },
  booked: {
    label: "Trip booked",
    tone: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blurb: "Payment received and held in escrow. Your transporter has been notified.",
  },
  in_transit: {
    label: "In transit",
    tone: "bg-indigo-50 border-indigo-200 text-indigo-800",
    blurb: "Your transporter is on the road. We'll notify you on delivery.",
  },
  delivered: {
    label: "Delivered",
    tone: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blurb: "Your transporter has marked the delivery complete. Confirm receipt to release escrow.",
  },
  completed: {
    label: "Complete",
    tone: "bg-emerald-50 border-emerald-200 text-emerald-800",
    blurb: "Trip complete. Thanks for using Armstrong Transport.",
  },
  cancelled: {
    label: "Cancelled",
    tone: "bg-red-50 border-red-200 text-red-800",
    blurb: "This trip was cancelled.",
  },
  refunded: {
    label: "Refunded",
    tone: "bg-amber-50 border-amber-200 text-amber-800",
    blurb: "This trip was refunded.",
  },
  partially_refunded: {
    label: "Partially refunded",
    tone: "bg-amber-50 border-amber-200 text-amber-800",
    blurb: "This trip's escrow was split between buyer and transporter.",
  },
  disputed: {
    label: "Disputed — awaiting resolution",
    tone: "bg-red-50 border-red-200 text-red-800",
    blurb: "A dispute is open on this trip. Escrow is frozen. Resolve by mutual agreement with the other party below, or the platform will refund the buyer in full after 30 days.",
  },
};

export default function TransportOrderDetail() {
  const { order_id } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const justPaid = searchParams.get("status") === "paid";
  const justSubmitted = searchParams.get("status") === "submitted";

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/transport/orders/${order_id}`);
      setOrder(res.data.order);
    } catch {
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [order_id]);

  useEffect(() => { load(); }, [load]);

  // Poll while status can still flip — admin assigns or webhook
  // marks paid. Stop polling once we reach a terminal-ish state.
  useEffect(() => {
    if (!order) return;
    const pollable = ["open_pending_assign", "open_for_bids", "awaiting_payment", "booked"];
    if (!pollable.includes(order.status)) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [order, load]);

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-4 py-10 space-y-4">
          <Skeleton className="h-12" />
          <Skeleton className="h-40" />
        </div>
      </AppShell>
    );
  }
  if (!order) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-4 py-10 text-center">
          <h1 className="font-spectral text-3xl">Order not found</h1>
          <p className="text-[#6B6B6B] mt-2">
            The link may have expired or been mistyped.
          </p>
          <Link to="/transport/request">
            <Button className="mt-4 rounded-full bg-ink text-white">
              Submit a new request
            </Button>
          </Link>
        </div>
      </AppShell>
    );
  }

  const pill = STATUS_PILL[order.status] || STATUS_PILL.open_pending_assign;

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 py-10" data-testid="transport-order-page">
        <Link to="/" className="text-[12px] text-[#6B6B6B] hover:text-ink">
          ← Back to Armstrong K9 Registry
        </Link>

        {(justSubmitted || justPaid) && (
          <div
            className="mt-3 border border-emerald-200 bg-emerald-50 rounded-xl p-4 flex items-start gap-3"
            data-testid="transport-order-banner"
          >
            <CheckCircle size={22} weight="duotone" className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-900 text-[14px]">
                {justPaid ? "Payment received" : "Request submitted"}
              </p>
              <p className="text-[12px] text-emerald-800 leading-relaxed mt-0.5">
                {justPaid
                  ? "Your funds are now held in escrow. Your transporter has been notified."
                  : "Save this page or check your email for updates as your trip is matched."}
              </p>
            </div>
          </div>
        )}

        <div className="flex items-baseline gap-3 mt-4 mb-1">
          <Truck size={32} weight="duotone" className="text-ink" />
          <h1 className="font-spectral text-4xl tracking-tight">
            Transport order
          </h1>
        </div>
        <p className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-6">
          ID {order.id}
        </p>

        {/* Buyer's refund-policy acknowledgement receipt — proves they
            ticked the "no refunds / no mediation" click-wrap when they
            opened the order. Only renders when the ack exists on the row. */}
        {order.refund_policy_acknowledged && order.refund_policy_version && (
          <p
            className="text-[10.5px] text-[#0F8043] -mt-2 mb-4 inline-flex items-center gap-1 font-semibold"
            data-testid={`transport-refund-ack-pill-${order.id}`}
            title={`Acknowledged v${order.refund_policy_version}`}
          >
            <ShieldCheck size={11} weight="fill" />
            Refund policy acknowledged · v{order.refund_policy_version}
            <a
              href="/refund#service-provider-bookings"
              target="_blank"
              rel="noreferrer"
              className="underline ml-1 hover:text-ink"
            >
              view
            </a>
          </p>
        )}

        <div
          className={`border rounded-2xl p-5 mb-6 ${pill.tone}`}
          data-testid={`status-pill-${order.status}`}
        >
          <p className="text-[11px] uppercase tracking-wider font-bold mb-1">
            {pill.label}
          </p>
          <p className="text-[14px] leading-relaxed">{pill.blurb}</p>
        </div>

        {/* Route summary */}
        <section className="border border-[#E5E5E5] bg-white rounded-2xl p-5 mb-4" data-testid="route-summary">
          <h2 className="font-spectral text-xl tracking-tight mb-3 flex items-center gap-2">
            <MapPin size={18} weight="duotone" className="text-ink" />
            Route
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Pickup</p>
              <p className="font-medium">
                {order.pickup_location.city}, {order.pickup_location.state}
              </p>
            </div>
            <div className="text-[#9A9A9A]">→</div>
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Dropoff</p>
              <p className="font-medium">
                {order.dropoff_location.city}, {order.dropoff_location.state}
              </p>
            </div>
          </div>
          {order.requested_pickup_date && (
            <p className="text-[12px] text-[#6B6B6B] mt-3 inline-flex items-center gap-1">
              <Clock size={12} /> Requested pickup: {order.requested_pickup_date}
              {order.flexibility_days > 0 && ` (±${order.flexibility_days} days)`}
            </p>
          )}
          <p className="text-[12px] text-[#6B6B6B] mt-1">
            {order.dog_count} dog{order.dog_count > 1 ? "s" : ""}
            {order.breed_size && ` · ${order.breed_size}`}
          </p>
        </section>

        {/* Transporter — once assigned */}
        {order.transporter_display_name && (
          <section className="border border-[#E5E5E5] bg-white rounded-2xl p-5 mb-4" data-testid="transporter-card">
            <h2 className="font-spectral text-xl tracking-tight mb-3 flex items-center gap-2">
              <ShieldCheck size={18} weight="duotone" className="text-emerald-600" />
              Your transporter
            </h2>
            <p className="font-spectral text-2xl">{order.transporter_display_name}</p>
            <p className="text-[12px] text-[#6B6B6B] mt-1">
              USDA-licensed · $300K+ commercial liability · Armstrong-verified
            </p>
          </section>
        )}

        {/* Pricing — once quoted */}
        {order.amount_usd && (
          <section className="border border-[#E5E5E5] bg-white rounded-2xl p-5 mb-4" data-testid="pricing-card">
            <h2 className="font-spectral text-xl tracking-tight mb-3 flex items-center gap-2">
              <CurrencyDollar size={18} weight="duotone" className="text-ink" />
              Pricing
            </h2>
            <div className="flex items-baseline justify-between">
              <span className="text-[#6B6B6B]">Total (escrow-protected)</span>
              <span className="font-spectral text-3xl">
                ${order.amount_usd.toFixed(2)}
              </span>
            </div>
            <p className="text-[11px] text-[#6B6B6B] mt-2 leading-relaxed">
              Funds release to your transporter 48 hours after delivery, or
              instantly when you confirm receipt. Refunded in full if your
              trip is cancelled outside the cancellation window.
            </p>
          </section>
        )}

        {/* CTA: pay (only when awaiting_payment) */}
        {order.status === "awaiting_payment" && order.stripe_checkout_url && (
          <a
            href={order.stripe_checkout_url}
            className="block rounded-full bg-crimson hover:bg-ink text-white text-center font-semibold py-3 transition-colors"
            data-testid="pay-now-button"
          >
            Pay ${order.amount_usd?.toFixed(2)} via Stripe →
          </a>
        )}

        {/* Bids list — buyer picks a winner */}
        {order.status === "open_for_bids" && (
          <BidsList orderId={order.id} onAccept={load} />
        )}

        {/* CTA: confirm receipt (when delivered or in_transit) */}
        {["delivered", "in_transit"].includes(order.status) && order.escrow_status !== "disputed" && (
          <ConfirmReceiptCard
            order={order}
            autoOpen={searchParams.get("confirm") === "1"}
            onChange={load}
          />
        )}

        {/* Trip Updates timeline — courier posts, buyer views, and
            buyer can copy a share link for family. Renders for any
            booked-and-beyond order; safety guardrails (EXIF strip,
            address redaction, no live GPS) live in the panel + backend. */}
        {["booked", "in_transit", "delivered", "completed"].includes(order.status) && (
          <section className="mt-6">
            <h3 className="font-spectral text-xl mb-3">Trip updates</h3>
            <TripUpdatesPanel tripKind="us_transport" tripId={order.id} />
          </section>
        )}

        {/* Phase 6 — buyer cancel + open dispute. Only available on
            active orders (pre-completion, not already cancelled/disputed).
            Authenticated endpoints, so we lazy-grab the token. */}
        {["open_for_bids", "open_pending_assign", "awaiting_payment", "booked", "in_transit", "delivered"].includes(order.status) && order.escrow_status !== "disputed" && (
          <ActionsCard order={order} onChange={load} />
        )}

        {/* Phase 6 — dispute status banner */}
        {order.dispute && (
          <DisputeBanner dispute={order.dispute} />
        )}

        {/* Path 1 venue posture (Iter 204) — consent-resolution panel
            shows up while a dispute is open and unresolved, lets buyer
            + transporter propose / accept outcomes without admin
            involvement. */}
        {order.dispute && !order.dispute.resolved_at && (
          <TransportConsentPanel order={order} onChange={load} />
        )}

        {/* Phase 6 — delay flag visibility */}
        {(order.delay_flags || []).some((f) => !f.cleared_at) && (
          <DelayFlagsBanner flags={order.delay_flags} />
        )}
      </div>
    </AppShell>
  );
}

/**
 * ConfirmReceiptCard — buyer one-tap "I received the dog" action.
 *
 * Rendered when the order is `delivered` (transporter marked it) or
 * `in_transit`. Auto-opens the confirmation modal when the buyer
 * arrives via `?confirm=1` from the transporter's delivery email.
 *
 * The actual POST hits the PUBLIC endpoint (UUID = access token) so
 * we never gate the confirm behind a registry account — first-time
 * buyers from `/transport/request` are common.
 */
function ConfirmReceiptCard({ order, autoOpen, onChange }) {
  const [open, setOpen] = React.useState(autoOpen);
  const [rating, setRating] = React.useState(5);
  const [note, setNote] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/transport/orders/${order.id}/confirm-delivery`, {
        rating,
        note: note || null,
      });
      toast.success("Receipt confirmed", {
        description: "Your transporter has been paid. Thank you!",
      });
      setOpen(false);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't confirm receipt.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="block w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-center font-semibold py-3 transition-colors"
        data-testid="confirm-receipt-button"
      >
        Confirm receipt → release payment
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm receipt of your dog</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-[#6B6B6B] leading-relaxed mb-1">
            This releases the escrowed payment to your transporter
            immediately. Only confirm if the delivery went well.
            If there's a problem, do NOT confirm — contact us first.
          </p>
          <form onSubmit={submit} className="space-y-4 mt-3" data-testid="confirm-receipt-form">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
                How was your transporter?
              </Label>
              <div className="flex gap-1 mt-2" data-testid="rating-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => setRating(n)}
                    className={`w-10 h-10 rounded-lg text-xl transition-colors ${
                      rating >= n
                        ? "bg-amber-100 border border-amber-300 text-amber-600"
                        : "bg-[#FAFAFA] border border-[#E5E5E5] text-[#9A9A9A] hover:bg-amber-50"
                    }`}
                    aria-label={`Rate ${n} stars`}
                    data-testid={`rating-star-${n}`}
                  >
                    ★
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
                Anything you want to share? (Optional)
              </Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="mt-1"
                placeholder="Quick note for your transporter..."
                data-testid="confirm-note"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-submit"
            >
              {submitting ? "Confirming…" : "Release payment & finish"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}


/**
 * BidsList — buyer-side bid comparison + accept UI.
 *
 * Polls /api/transport/orders/:id/bids every 10 seconds while the
 * order is in `open_for_bids` (handled by the parent's polling loop;
 * we just refetch on every parent reload). Shows each verified
 * transporter's quote with their name, trips_completed, service area,
 * and an Accept button. Accepting creates the Stripe Checkout and the
 * parent component re-renders with the Pay CTA.
 */
function BidsList({ orderId, onAccept }) {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/transport/orders/${orderId}/bids`);
      setBids(res.data.bids || []);
    } catch {
      setBids([]);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const accept = async (quoteId) => {
    if (!window.confirm("Accept this bid and continue to payment?")) return;
    setAccepting(quoteId);
    try {
      const res = await api.post(
        `/transport/orders/${orderId}/accept-bid/${quoteId}`,
        { origin_url: webOrigin() }
      );
      toast.success("Bid accepted — redirecting to Stripe");
      // Drop into the Stripe checkout immediately.
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        onAccept?.();
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't accept this bid.");
    } finally {
      setAccepting(null);
    }
  };

  return (
    <section className="border border-[#E5E5E5] bg-white rounded-2xl p-5 mb-4" data-testid="bids-list">
      <h2 className="font-spectral text-xl tracking-tight mb-3">
        Quotes received {bids.length > 0 && (
          <span className="text-[12px] text-[#6B6B6B] ml-1">({bids.length})</span>
        )}
      </h2>
      {loading ? (
        <Skeleton className="h-24" />
      ) : bids.length === 0 ? (
        <p className="text-[13px] text-[#6B6B6B] py-4 text-center">
          No quotes yet. Verified transporters have been notified — most respond within 24 hours.
        </p>
      ) : (
        <div className="space-y-3">
          {bids.map(({ quote, transporter }, idx) => {
            const isCheapest = idx === 0;  // backend sorts by amount ASC
            return (
              <div
                key={quote.id}
                className={`border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                  isCheapest ? "border-emerald-300 bg-emerald-50/30" : "border-[#EAEAEA] bg-white"
                }`}
                data-testid={`bid-row-${quote.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <p className="font-spectral text-lg">{transporter?.display_name || "Verified hauler"}</p>
                    {isCheapest && bids.length > 1 && (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-emerald-100 border border-emerald-300 text-emerald-800">
                        Lowest price
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-[#6B6B6B] mt-0.5">
                    {transporter?.trips_completed || 0} completed trips · USDA-licensed · Armstrong-verified
                  </p>
                  {quote.notes && (
                    <p className="text-[12px] text-[#444] italic mt-1">"{quote.notes}"</p>
                  )}
                  {quote.pickup_eta && (
                    <p className="text-[11px] text-[#9A9A9A] mt-0.5">
                      Pickup ETA: {new Date(quote.pickup_eta).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="font-spectral text-2xl">${quote.amount_usd.toFixed(2)}</p>
                  <Button
                    size="sm"
                    disabled={accepting === quote.id}
                    onClick={() => accept(quote.id)}
                    className="mt-1 rounded-full bg-crimson hover:bg-ink text-white text-[12px] h-8 px-4"
                    data-testid={`accept-bid-${quote.id}`}
                  >
                    {accepting === quote.id ? "Accepting…" : "Accept & pay"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}


/**
 * ActionsCard — buyer-side cancel + dispute actions (Phase 6).
 *
 * Both actions hit authenticated `/me/transport/orders/...` endpoints
 * so the user must be signed in with the same email they used at
 * checkout. We grab the token off `localStorage.token` (same axios
 * interceptor convention used elsewhere). Anonymous buyers see a
 * "Sign in to manage this trip" hint instead.
 */
function ActionsCard({ order, onChange }) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  if (!token) {
    return (
      <p className="text-[12px] text-[#6B6B6B] text-center mt-4" data-testid="anon-actions-hint">
        Sign in with <strong>{order.buyer_email}</strong> to cancel or open a dispute on this trip.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-2 mt-4" data-testid="buyer-actions">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setCancelOpen(true)}
        className="rounded-full text-[12px] h-9 px-4"
        data-testid="buyer-cancel-trigger"
      >
        Cancel trip
      </Button>
      {["booked", "in_transit", "delivered"].includes(order.status) && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDisputeOpen(true)}
          className="rounded-full text-[12px] h-9 px-4 border-crimson/40 text-crimson hover:bg-crimson hover:text-white"
          data-testid="buyer-dispute-trigger"
        >
          Open a dispute
        </Button>
      )}
      <CancelDialog
        open={cancelOpen} onOpenChange={setCancelOpen}
        order={order} onChange={onChange}
      />
      <DisputeDialog
        open={disputeOpen} onOpenChange={setDisputeOpen}
        order={order} onChange={onChange}
      />
    </div>
  );
}

function CancelDialog({ open, onOpenChange, order, onChange }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/me/transport/orders/${order.id}/cancel`,
        { reason: reason || null });
      const { tier, kill_fee_usd, refund_usd } = res.data;
      toast.success(
        tier === "free"
          ? "Trip cancelled — full refund."
          : `Trip cancelled. Kill fee: $${kill_fee_usd?.toFixed(2)}, refund: $${refund_usd?.toFixed(2)}.`,
      );
      onOpenChange(false);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't cancel.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel this trip?</DialogTitle>
        </DialogHeader>
        <p className="text-[13px] text-[#6B6B6B] leading-relaxed mb-2" data-testid="cancel-policy">
          Cancellation refunds depend on lead time:
        </p>
        <ul className="text-[12px] text-[#444] space-y-1 mb-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-3">
          <li>· <strong>More than 72 hours</strong> before pickup: full refund.</li>
          <li>· <strong>24–72 hours</strong> before pickup: 25% kill fee to transporter.</li>
          <li>· <strong>Less than 24 hours</strong> before pickup: 50% kill fee.</li>
          <li>· Already delivered or completed: must use dispute flow instead.</li>
        </ul>
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
          Reason (optional)
        </Label>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="mt-1"
          placeholder="A quick note helps us improve the marketplace…"
          data-testid="cancel-reason"
        />
        <Button
          onClick={submit}
          disabled={submitting}
          className="w-full mt-3 rounded-full bg-crimson hover:bg-ink text-white"
          data-testid="cancel-submit"
        >
          {submitting ? "Cancelling…" : "Confirm cancellation"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function DisputeDialog({ open, onOpenChange, order, onChange }) {
  const [reason, setReason] = useState("");
  const [evidenceText, setEvidenceText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 10) {
      toast.error("Please describe the issue in at least 10 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/me/transport/orders/${order.id}/dispute`, {
        reason,
        evidence_urls: evidenceText
          .split(/\s|,/).map((s) => s.trim()).filter(Boolean).slice(0, 10),
      });
      toast.success("Dispute opened — escrow frozen", {
        description: "We'll review within 72 business hours and email you a resolution.",
      });
      onOpenChange(false);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't open dispute.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Open a dispute</DialogTitle>
        </DialogHeader>
        <p className="text-[13px] text-[#6B6B6B] leading-relaxed mb-3">
          Opening a dispute freezes the transporter's payout immediately.
          Armstrong reviews the evidence within 72 business hours and
          either releases, refunds, or splits the escrow.
        </p>
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
          What went wrong? *
        </Label>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          className="mt-1 mb-3"
          placeholder="Describe what happened with as much detail as possible…"
          data-testid="dispute-reason"
        />
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
          Evidence URLs (photo / video links, space- or comma-separated)
        </Label>
        <Textarea
          value={evidenceText}
          onChange={(e) => setEvidenceText(e.target.value)}
          rows={2}
          className="mt-1"
          placeholder="https://imgur.com/..."
          data-testid="dispute-evidence"
        />
        <Button
          onClick={submit}
          disabled={submitting}
          className="w-full mt-3 rounded-full bg-crimson hover:bg-ink text-white"
          data-testid="dispute-submit"
        >
          {submitting ? "Opening…" : "Open dispute (freezes escrow)"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function DisputeBanner({ dispute }) {
  const resolved = !!dispute.resolved_at;
  return (
    <section
      className={`border rounded-2xl p-5 mt-4 ${resolved ? "border-amber-200 bg-amber-50" : "border-red-200 bg-red-50"}`}
      data-testid={resolved ? "dispute-banner-resolved" : "dispute-banner-open"}
    >
      <p className="text-[11px] uppercase tracking-wider font-bold mb-1">
        {resolved ? "Dispute resolved" : "Dispute open — awaiting mutual agreement"}
      </p>
      <p className="text-[13px] leading-relaxed">
        Opened by <strong>{dispute.opened_by}</strong> ·{" "}
        {new Date(dispute.opened_at).toLocaleDateString()}
      </p>
      {dispute.reason && (
        <p className="text-[12px] mt-2 italic text-[#444]">"{dispute.reason}"</p>
      )}
      {resolved && dispute.resolution && (
        <div className="mt-2 pt-2 border-t border-amber-300/50 text-[12px] text-[#444]">
          <strong>Outcome:</strong> {dispute.resolution.action}
          {dispute.resolution.buyer_refund_pct != null && (
            <span> · {dispute.resolution.buyer_refund_pct}% refund to buyer</span>
          )}
          {dispute.resolution.note && (
            <p className="mt-1 italic">"{dispute.resolution.note}"</p>
          )}
        </div>
      )}
    </section>
  );
}

function DelayFlagsBanner({ flags }) {
  const active = (flags || []).filter((f) => !f.cleared_at);
  if (active.length === 0) return null;
  return (
    <section
      className="border border-amber-200 bg-amber-50 rounded-2xl p-5 mt-4"
      data-testid="delay-flags-banner"
    >
      <p className="text-[11px] uppercase tracking-wider font-bold text-amber-900 mb-1">
        Delivery delay reported
      </p>
      <p className="text-[13px] text-amber-800 leading-relaxed mb-2">
        Your transporter flagged a delay. The escrow auto-release clock is paused until the issue clears.
      </p>
      <ul className="text-[12px] text-[#444] space-y-1">
        {active.map((f) => (
          <li key={f.id}>
            · <strong className="capitalize">{f.reason}</strong>
            {f.note && <span> — {f.note}</span>}
            <span className="text-[#9A9A9A] ml-1">
              ({new Date(f.raised_at).toLocaleString()})
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}



/**
 * TransportConsentPanel — Path-1 venue-posture wrapper around the
 * shared ConsentResolutionPanel. Resolves the labels based on
 * whether the signed-in user is the buyer or the transporter (so
 * the panel can address the counterparty correctly), and points the
 * propose/accept URLs at the transport-specific endpoints.
 */
function TransportConsentPanel({ order, onChange }) {
  const { user } = useAuth();
  const userEmail = (user?.email || "").toLowerCase();
  const buyerEmail = (order.buyer_email || "").toLowerCase();
  const transporterUserId = order.transporter_user_id;  // surface this from the order if present
  const isBuyer = !!(userEmail && buyerEmail && userEmail === buyerEmail);
  const isTransporter = !!(user && transporterUserId && user.id === transporterUserId);
  if (!isBuyer && !isTransporter) return null;  // observers see nothing

  return (
    <section
      className="bg-white border border-[#E5E5E5] rounded-xl p-5 shadow-luxury-light"
      data-testid="transport-consent-section"
    >
      <h2 className="font-spectral text-2xl tracking-tight mb-1">
        Resolve this dispute
      </h2>
      <p className="text-[13px] text-[#6B6B6B] leading-relaxed">
        Propose an outcome. Both parties have to agree for any
        resolution to execute. If you can&apos;t reach agreement, the
        platform will mechanically refund the buyer in full after 30
        days. Armstrong K9 Registry does not adjudicate transport
        disputes.
      </p>
      <ConsentResolutionPanel
        proposeUrl={`/transport/orders/${order.id}/consent/propose`}
        acceptUrl={`/transport/orders/${order.id}/consent/accept`}
        proposals={(order.dispute || {}).consent_proposals || []}
        currentUserId={user?.id}
        myRoleLabel={isBuyer ? "buyer" : "transporter"}
        counterpartyLabel={isBuyer ? "the transporter" : "the buyer"}
        disputeFiledAt={(order.dispute || {}).opened_at}
        onResolved={onChange}
        testIdPrefix={`transport-consent-${order.id}`}
      />
    </section>
  );
}
